import { useState } from 'react'
import { Button } from '../../platform/primitives/Button'
import { useAuth } from '../../auth/useAuth'
import { AuthCard } from './AuthCard'
import './auth.css'

/**
 * Firebase said yes; our backend did not answer.
 *
 * CLAUDE.md: an error says whose fault and the recovery path. The user's
 * credentials are fine and their account is fine — saying so is the point,
 * because the alternative reading is "my account is broken". This is also why
 * it is one screen rather than an error inside each panel: the cause is common
 * to all of them, and six symptoms do not add up to a diagnosis.
 *
 * Two recovery paths, because there are genuinely two. A retry is right when
 * the service is briefly down; signing out is right when the user wants to try
 * a different account, and without it this screen is a room with no doors —
 * `RequireAuth` renders it for every chrome route, so there is nowhere to
 * navigate to.
 */
export function SessionUnavailable() {
  const auth = useAuth()
  const [retrying, setRetrying] = useState(false)
  const [leaving, setLeaving] = useState(false)

  return (
    <AuthCard title="Signed in, but we can't reach TrackE5">
      <p className="auth-card__note">
        Your account is fine — the TrackE5 service didn't respond. This is usually temporary.
      </p>
      <Button
        variant="primary"
        size="page"
        disabled={retrying}
        onClick={() => {
          setRetrying(true)
          // Cleared on both paths, unlike sign-out: a retry that fails leaves
          // this same screen mounted, so a `busy` that is never reset would
          // disable the only control that can recover.
          void auth.retrySession().finally(() => setRetrying(false))
        }}
      >
        {retrying ? 'Trying…' : 'Try again'}
      </Button>
      <Button
        variant="outline"
        size="page"
        disabled={leaving}
        onClick={() => {
          setLeaving(true)
          // Reset on failure, exactly as `AccountMenu` does — and this is the
          // screen where the failure is LIKELY rather than exotic, since its
          // whole premise is that the service is not responding. There is also
          // nowhere to navigate: the guard renders this for every chrome route,
          // so a control that disabled itself and stayed disabled would strand
          // the user on the one screen with no way off it.
          void auth.signOut().catch(() => setLeaving(false))
        }}
      >
        {leaving ? 'Signing out…' : 'Sign out'}
      </Button>
    </AuthCard>
  )
}
